/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.wrapper = this.closest('.pagination--wrapper');
      this.grid = document.querySelector(`[data-id="${id}"]`);
      this.page = 1;
      this.page_size = this.dataset.pageSize;
      this.stateKey = null;
      this.scrollListener = null;
      this.restoringState = false;
      this.scrollSaveScheduled = false;
    }

    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.initLoadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.initInfiniteScroll();
      }
    }

    disconnectedCallback() {
      this.teardownStateManagement();
    }

    addUrlParam(search, key) {
      const newParam = `${key}=${this.page}`;
      if (!search) return `?${newParam}`;

      const params = search.replace(new RegExp(`([?&])${key}[^&]*`), `$1${newParam}`);
      return params === search ? `${search}&${newParam}` : params;
    }

    initLoadMore() {
      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    initInfiniteScroll() {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].intersectionRatio === 1) {
          this.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      this.observer.observe(this);
      this.initStateManagement();
    }

    async loadProducts(options = {}) {
      if (this.hasAttribute('loading')) return;
      const { skipStateUpdate = false } = options;

      this.page++;
      this.setAttribute('loading', 'true');
      const url = `${document.location.pathname}${this.addUrlParam(document.location.search, 'page')}`;

      try {
        const response = await fetch(url);
        const responseText = await response.text();
        this.renderProducts(responseText, url);
        this.renderCounts(responseText);

        if (!skipStateUpdate) {
          this.saveState();
        }
        dispatchCustomEvent('pagination:page-changed', { url });
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        this.removeAttribute('loading');
      }
    }

    renderProducts(html, url) {
      const gridToReplace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
      if (!gridToReplace) {
        if (this.observer) this.observer.unobserve(this);
        if (this.button) this.button.classList.add('visually-hidden');
        return;
      }

      const products = gridToReplace.querySelectorAll('.columns');
      products.forEach((product) => {
        this.grid.appendChild(product);
        product.querySelectorAll('.lazyload').forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });
      if (products.length < this.page_size) {
        this.hide();
      }

    }
    renderCounts(html) {
      const newCounts = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');

      if (!newCounts) {
        return;
      }
      this.wrapper.querySelector('#ProductCount').innerHTML = newCounts.innerHTML;
    }

    hide() {
      this.button.style.display = 'none';
    }

    initStateManagement() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        this.stateKey = `pagination-state:${url.pathname}${url.search}`;
      } catch (error) {
        this.stateKey = null;
      }

      if (!this.stateKey) {
        return;
      }

      const storageTestKey = `${this.stateKey}-test`;
      try {
        window.sessionStorage.setItem(storageTestKey, '1');
        window.sessionStorage.removeItem(storageTestKey);
      } catch (error) {
        this.stateKey = null;
        return;
      }

      this.scrollListener = () => {
        if (this.restoringState) return;
        if (this.scrollSaveScheduled) return;
        this.scrollSaveScheduled = true;
        requestAnimationFrame(() => {
          this.scrollSaveScheduled = false;
          this.saveState();
        });
      };
      window.addEventListener('scroll', this.scrollListener, { passive: true });
      this.restoreStateIfNeeded();
    }

    teardownStateManagement() {
      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
      }
      this.scrollSaveScheduled = false;
    }

    getSavedState() {
      if (!this.stateKey) return null;
      try {
        const state = window.sessionStorage.getItem(this.stateKey);
        return state ? JSON.parse(state) : null;
      } catch (error) {
        return null;
      }
    }

    saveState() {
      if (!this.stateKey || this.restoringState) return;
      try {
        const state = {
          page: this.page,
          scrollY: window.scrollY,
          url: window.location.pathname + window.location.search
        };
        window.sessionStorage.setItem(this.stateKey, JSON.stringify(state));
      } catch (error) {
        // Fail silently if sessionStorage is not available
      }
    }

    async restoreStateIfNeeded() {
      const state = this.getSavedState();
      if (!state) return;

      this.restoringState = true;
      const targetPage = Number(state.page) || 1;
      if (targetPage > this.page) {
        while (this.page < targetPage) {
          await this.loadProducts({ skipStateUpdate: true });
        }
      }
      this.restoringState = false;

      if (typeof state.scrollY === 'number' && state.scrollY > 0) {
        requestAnimationFrame(() => {
          window.scrollTo(0, state.scrollY);
        });
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}